import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { copyImageToClipboard } from 'copy-image-clipboard';
import html2canvas from 'html2canvas';
import { NotificationManager } from '../toast';
import { ChartOrderItem } from '../../utils/types/analytics/analytics_types';

type DashletIconGroupProps = {
  id: string;
  dashletItem: ChartOrderItem;
  setFullScreenDetected?: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const DashletIconGroup = ({
  id,
  dashletItem,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: DashletIconGroupProps) => {
  const handleCopyDashlet = async () => {
    const element = document.getElementById(id);
    if (element) {
      const canvas = await html2canvas(element);
      const data = canvas.toDataURL('image/jpg');
      copyImageToClipboard(data)
        .then(() => {
          NotificationManager.success('Image Copied!');
        })
        .catch(() => {
          NotificationManager.error('Unable to copy image!');
        });
    }
  };

  const handleFullScreen = () => {
    const elem: any = document.getElementById(id);
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
        if (setFullScreenDetected) {
          setFullScreenDetected(true);
        }
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
        if (setFullScreenDetected) {
          setFullScreenDetected(true);
        }
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
        if (setFullScreenDetected) {
          setFullScreenDetected(true);
        }
      }
    }
  };

  const updateDashletVisibility = () => {
    const editedItem: ChartOrderItem = {
      chartId: dashletItem.chartId,
      hidden: !dashletItem.hidden,
    };
    handleDashletVisibility(editedItem);
  };

  return (
    <Box
      className="dashlet-toolbar"
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 10,
        opacity: 0,
        transition: 'opacity 0.2s ease',
        '.group:hover &': {
          opacity: 1,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          bgcolor: 'background.paper',
          p: 0.5,
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Fullscreen" arrow>
          <IconButton size="small" onClick={handleFullScreen}>
            <FullscreenRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy as Image" arrow>
          <IconButton size="small" onClick={handleCopyDashlet}>
            <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {editMode && (
          <Tooltip title={dashletItem.hidden ? 'Show' : 'Hide'} arrow>
            <IconButton size="small" onClick={updateDashletVisibility}>
              {dashletItem.hidden ? (
                <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
